import { useState, useEffect, useRef } from "react";
import MessageBubble from "./MessageBubble";
import { useWebSocketActions } from "../../context/WebSocketContext";
import { useTranslation } from "react-i18next";
import { ArrowLeft, SendHorizontal, Camera } from "lucide-react";

const ChatAvatar = ({ friend, iniciales }) => {
  const [imagenFallida, setImagenFallida] = useState(false);

  const foto =
    friend?.photo ||
    friend?.foto_perfil_url ||
    friend?.foto_perfil ||
    friend?.profile_image_url ||
    friend?.profile_image ||
    friend?.avatar_url ||
    friend?.avatar ||
    friend?.image_url ||
    friend?.foto ||
    null;

  const fotoUrl =
    typeof foto === "string"
      ? foto
      : foto?.url || foto?.image || null;

  const urlCompleta = fotoUrl
    ? fotoUrl.startsWith("http") || fotoUrl.startsWith("data:")
      ? fotoUrl
      : fotoUrl.startsWith("/")
        ? `${window.location.origin}${fotoUrl}`
        : `${window.location.origin}/${fotoUrl}`
    : null;

  useEffect(() => {
    setImagenFallida(false);
  }, [urlCompleta]);

  if (urlCompleta && !imagenFallida) {
    return (
      <img
        src={urlCompleta}
        alt={`Foto de ${friend?.nombre || "contacto"}`}
        className="h-10 w-10 shrink-0 rounded-full border border-slate-200 bg-slate-100 object-cover"
        onError={() => setImagenFallida(true)}
      />
    );
  }

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#dcecf5] text-sm font-bold text-[#245580]">
      {iniciales}
    </div>
  );
};

const Conversation = ({ friend, messages, onSend, onSendPhoto, onPhotoDeleted, currentUserId, onBackMobile }) => {
  const [text, setText] = useState("");
  // HAPPYCAP_CHAT_SCROLL_BOTTOM_V2
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const { sendJsonMessage } = useWebSocketActions();
  const { t } = useTranslation();

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const scrollToBottom = () => {
      container.scrollTop = container.scrollHeight;
    };

    // Desplazamiento inmediato al último mensaje.
    scrollToBottom();

    // Reajustar después de que el navegador pinte el contenido.
    const frame = requestAnimationFrame(scrollToBottom);

    // Reajustar cuando las fotografías cambien de tamaño.
    const observer = new ResizeObserver(scrollToBottom);
    observer.observe(container.firstElementChild || container);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [messages, friend?.id]);

  const nombreCompleto = `${friend.nombre || ""} ${friend.apellidos || ""}`.trim();

  const iniciales = nombreCompleto
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join("") || "?";

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-[#f4f7fa]">

      {/* Cabecera */}
      <div className="z-10 flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-3 py-3 shadow-sm sm:px-5">

        <button
          type="button"
          onClick={onBackMobile}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[#245580] transition hover:bg-slate-100 md:hidden"
          aria-label="Volver"
        >
          <ArrowLeft size={21} />
        </button>

        <ChatAvatar friend={friend} iniciales={iniciales} />

        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold text-[#245580]">
            {nombreCompleto}
          </div>
        </div>
      </div>

      {/* Mensajes */}
      <div
        ref={messagesContainerRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-4 sm:px-5"
      >
        <div className="mx-auto flex w-full max-w-4xl flex-col">

          {messages.map((msg, i) => {
            const senderName = `${msg.sender_nombre ?? msg.sender?.nombre ?? ""} ${msg.sender_apellidos ?? msg.sender?.apellidos ?? ""}`.trim();

            return (
              <MessageBubble
                key={`${msg.id || "temp"}-${msg.timestamp || i}`}
                message={msg}
                isOwn={msg.isOwn}
                senderName={!msg.isOwn ? senderName : null}
                onPhotoDeleted={onPhotoDeleted}
              />
            );
          })}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Campo de escritura */}
      <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-3 sm:px-5">
        <form
          onSubmit={handleSubmit}
          className="mx-auto flex w-full max-w-4xl items-center gap-2"
        >

          {/* HAPPYCAP_CHAT_CAMERA_V1 */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={async (event) => {
              const file = event.target.files?.[0];
              event.target.value = "";

              if (!file || !onSendPhoto) return;

              setUploadingPhoto(true);

              try {
                await onSendPhoto(file);
              } catch (error) {
                console.error("Error enviando fotografía:", error);
                alert("No se pudo enviar la fotografía.");
              } finally {
                setUploadingPhoto(false);
              }
            }}
          />

          <button
            type="button"
            disabled={uploadingPhoto || !onSendPhoto}
            onClick={() => photoInputRef.current?.click()}
            title="Enviar fotografía"
            aria-label="Enviar fotografía"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-[#247BA8] transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Camera size={20} />
          </button>

          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("chat.write_message", "Escribe un mensaje...")}
            className="min-w-0 flex-1 rounded-full border border-slate-200 bg-[#f5f7f9] px-4 py-3 text-base text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15 sm:text-sm"
          />

          <button
            type="submit"
            disabled={!text.trim()}
            title={t("chat.send", "Enviar")}
            aria-label={t("chat.send", "Enviar")}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#16A34A] text-white shadow-sm transition hover:bg-[#15803D] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <SendHorizontal size={19} />
          </button>

        </form>
      </div>
    </div>
  );
};

export default Conversation;
